/**
 * Valuation Plus Basis Calculator
 *
 * For TSG (The Symmetrical Group, isTSG) - hybrid calculation where value equals
 * standard valuation (in-the-money) PLUS cost basis (if positive).
 *
 * From index.js line 6150:
 *   if (isTSG) shareKeys.forEach(k => latestValue[k] += bases[k] > 0 ? bases[k] : 0);
 *
 * This adds basis to the existing in-the-money valuation, but only if basis > 0.
 * Used for "sponsor scenario" calculations where both market value and invested
 * capital are relevant.
 */

const ValuationPlusBasisCalculator = {
	methodId: 'valuation_plus_basis',
	displayName: 'Valuation + Basis (Hybrid)',
	requiredDataSources: ['valuations', 'bases'],

	// Graph configuration - line graph shows combined valuation + basis over time
	graphConfig: {
		recommendedType: 'time_series_line',
		timeSeriesStrategy: 'valuation_based',
		supportsTimeSeries: true,
	},

	/**
	 * Calculate hybrid value: valuation + positive basis
	 * @param {Object} input
	 * @param {Object} input.holdings - { owned, sharePrice, strikePrice, bases } by class
	 * @param {string[]} input.shareClasses - Array of share class keys
	 * @returns {Object} CalculationResult
	 */
	calculate: function (input) {
		const holdings = input.holdings;
		const shareClasses = input.shareClasses || Object.keys(holdings.owned || {});

		let totalValue = 0;
		let totalValuationValue = 0;
		let totalBasisAddition = 0;
		const breakdown = [];

		shareClasses.forEach(function (key) {
			const owned = holdings.owned[key] || 0;
			const sharePrice = holdings.sharePrice[key] || 0;
			const strikePrice = holdings.strikePrice[key] || 0;
			const bases = holdings.bases[key] || 0;

			// Standard in-the-money valuation
			const valuationValue = owned * (sharePrice - strikePrice);

			// TSG formula: only add basis if positive
			// From line 6150: latestValue[k] += bases[k] > 0 ? bases[k] : 0
			const basisAddition = bases > 0 ? bases : 0;

			const value = valuationValue + basisAddition;

			totalValuationValue += valuationValue;
			totalBasisAddition += basisAddition;
			totalValue += value;

			breakdown.push({
				classKey: key,
				className: key,
				value: value,
				valuationValue: valuationValue,
				basisAddition: basisAddition,
				owned: owned,
				sharePrice: sharePrice,
				strikePrice: strikePrice,
				bases: bases,
			});
		});

		return {
			totalValue: totalValue,
			totalValuationValue: totalValuationValue,
			totalBasisAddition: totalBasisAddition,
			breakdown: breakdown,
			fundMetrics: null,
			metadata: {
				methodUsed: this.methodId,
				calculationDate: new Date(),
			},
		};
	},
};

export { ValuationPlusBasisCalculator };
